import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import type { RefObject } from 'react'

export interface ScrollSpy<TKey extends string> {
  activeId: TKey
  registerSection: (key: TKey, el: HTMLElement | null) => void
  scrollToSection: (key: TKey) => void
}

/**
 * Fraction of the scroll container's height, measured from its top, that marks
 * the activation line. A section becomes active once its top edge scrolls at or
 * above this line — i.e. once its heading sits in the upper quarter of the view.
 */
const ACTIVATION_FRACTION = 0.25

/** Scroll positions within this many px are treated as the same (sub-pixel). */
const ARRIVAL_TOLERANCE_PX = 2

/**
 * The scrollTop the container would have once `el` is scrolled to the top.
 * Computed from bounding rects (same coordinate space as the activation-line
 * math) rather than `el.offsetTop`, which is relative to the nearest positioned
 * ancestor and may not be the scroll container. Clamped to the container's max
 * scroll so a short final section (which rests at the bottom) still resolves.
 */
const intendedScrollTop = (container: HTMLElement, el: HTMLElement): number => {
  const delta =
    el.getBoundingClientRect().top - container.getBoundingClientRect().top
  const maxScroll = container.scrollHeight - container.clientHeight
  return Math.min(
    Math.max(container.scrollTop + delta, 0),
    Math.max(maxScroll, 0)
  )
}

/**
 * Scroll-spy that derives the active section deterministically from scroll
 * position rather than from IntersectionObserver fire-order.
 *
 * On manual scroll/resize it picks the LAST registered section (in `keys` order)
 * whose top edge has crossed the activation line — a pure function of scroll
 * position, so it never flickers. The bottom of the list is handled by the same
 * math via a clamp: at max scroll the last section wins even if (being short) it
 * never pushes its top above the line.
 *
 * On a sidebar click, `scrollToSection` holds the active id on the clicked
 * target and suppresses intermediate-section tracking until the smooth scroll
 * ARRIVES (arrival detected by scroll position vs the target's resting offset —
 * not a timer). A user wheel/touchmove gesture releases that lock immediately.
 *
 * `keys` defines section order and the default active key. `root` is the
 * scrollable container; sections register their elements via `registerSection`.
 */
export function useScrollSpy<TKey extends string>(
  keys: readonly TKey[],
  root?: RefObject<HTMLElement | null>
): ScrollSpy<TKey> {
  const [activeId, setActiveId] = useState<TKey>(keys[0])
  const elements = useRef<Map<TKey, HTMLElement>>(new Map())

  // When a click triggers a smooth scroll, hold the active id on the clicked
  // target and stop tracking intermediate sections until the scroll ARRIVES at
  // the target (detected by scroll position, not a timer). This keeps the
  // clicked item highlighted through the animation instead of the highlight
  // travelling through every section it passes.
  const pendingTarget = useRef<TKey | null>(null)

  // Keep the latest keys in a ref so the scroll handler reads current order
  // without being re-created (and re-bound) on every render.
  const keysRef = useRef(keys)
  keysRef.current = keys

  // Stable dep: re-run effects only when the set/order of keys actually changes,
  // not when the caller passes a fresh inline array literal each render.
  const keysKey = keys.join(',')

  // Derive the active key from the container's current scroll position. The
  // active section is the last one whose top edge (relative to the container's
  // viewport) has reached the activation line. Pure w.r.t. scroll position.
  const computeActive = useCallback(() => {
    const container = root?.current
    if (!container) {
      return
    }
    const orderedKeys = keysRef.current
    if (orderedKeys.length === 0) {
      return
    }

    // A click-scroll is in flight: keep the clicked target active and skip
    // position tracking until we've arrived at it. Arrival = the container's
    // scrollTop has reached the target's resting scroll offset (clamped to the
    // max scroll, since the last/short section rests at the bottom). Pure
    // position check — no timing assumptions.
    const target = pendingTarget.current
    if (target !== null) {
      const targetEl = elements.current.get(target)
      if (targetEl) {
        const arrived =
          Math.abs(
            container.scrollTop - intendedScrollTop(container, targetEl)
          ) <= ARRIVAL_TOLERANCE_PX
        if (!arrived) {
          return
        }
      }
      // Arrived (or target unknown) — resume normal tracking.
      pendingTarget.current = null
    }

    let resolved: TKey

    // Bottom clamp: when the container is scrolled to its maximum, the last
    // section is the active one by definition — you cannot scroll further to
    // bring it past the activation line. A short final section (which can never
    // reach the line) would otherwise leave an earlier section incorrectly
    // selected. This is position-truth, not a timing hack.
    const maxScroll = container.scrollHeight - container.clientHeight
    const atBottom =
      maxScroll > 0 && container.scrollTop >= maxScroll - ARRIVAL_TOLERANCE_PX

    if (atBottom) {
      resolved = orderedKeys[orderedKeys.length - 1]
    } else {
      // Otherwise the active section is the lowest one whose top edge has
      // crossed the activation line (a fixed fraction down the container).
      const activationLine =
        container.getBoundingClientRect().top +
        container.clientHeight * ACTIVATION_FRACTION

      let nextActive: TKey | null = null
      for (const key of orderedKeys) {
        const el = elements.current.get(key)
        if (!el) {
          continue
        }
        if (el.getBoundingClientRect().top <= activationLine + 1) {
          nextActive = key
        } else {
          break
        }
      }
      // Before the first section reaches the line, default to the first key.
      resolved = nextActive ?? orderedKeys[0]
    }

    setActiveId((prev) => (prev === resolved ? prev : resolved))
  }, [root])

  // Bind scroll + resize listeners to the container and recompute on changes.
  useEffect(() => {
    const container = root?.current
    if (!container) {
      return
    }

    const onScroll = () => computeActive()
    container.addEventListener('scroll', onScroll, { passive: true })

    // A user-initiated scroll gesture cancels any in-flight click lock so the
    // highlight immediately resumes tracking their manual scroll, even if they
    // interrupt the click animation. (Definitive user intent, not a guess.)
    const releaseLock = () => {
      if (pendingTarget.current !== null) {
        pendingTarget.current = null
        computeActive()
      }
    }
    container.addEventListener('wheel', releaseLock, { passive: true })
    container.addEventListener('touchmove', releaseLock, { passive: true })

    // Recompute when the container or its content resizes (sections mounting,
    // window resize, font load) so the active id stays correct without scroll.
    const resizeObserver = new ResizeObserver(() => computeActive())
    resizeObserver.observe(container)
    if (container.firstElementChild) {
      resizeObserver.observe(container.firstElementChild)
    }

    computeActive()

    return () => {
      container.removeEventListener('scroll', onScroll)
      container.removeEventListener('wheel', releaseLock)
      container.removeEventListener('touchmove', releaseLock)
      resizeObserver.disconnect()
    }
    // computeActive is stable (deps: root); keysKey re-runs when sections change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysKey, computeActive])

  // Recompute once after layout on mount so the initial active id is correct
  // even if no scroll/resize has fired yet.
  useLayoutEffect(() => {
    computeActive()
  }, [computeActive])

  const registerSection = useCallback(
    (key: TKey, el: HTMLElement | null) => {
      if (!el) {
        elements.current.delete(key)
        return
      }
      elements.current.set(key, el)
      // A late-registering section can change which key is active at the
      // current scroll position; recompute so the highlight stays correct.
      computeActive()
    },
    [computeActive]
  )

  const scrollToSection = useCallback(
    (key: TKey) => {
      const container = root?.current
      const targetEl = elements.current.get(key)

      // Only lock (suppress intermediate tracking until arrival) when the scroll
      // will actually move. If the container is already at the target's resting
      // position no scroll event fires, so a lock would never get an arrival to
      // release it and would freeze tracking — skip the lock in that case.
      const willScroll =
        !container ||
        !targetEl ||
        Math.abs(container.scrollTop - intendedScrollTop(container, targetEl)) >
          ARRIVAL_TOLERANCE_PX

      pendingTarget.current = willScroll ? key : null
      setActiveId(key)
      // Always request the scroll — it's a harmless no-op when already in place.
      targetEl?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    [root]
  )

  return { activeId, registerSection, scrollToSection }
}
